<template>
    <b-container class="h-100 layout-container" v-show="loaded">
        <b-row class="h-100 justify-content-around align-items-center">
            <b-col cols="12">
                <div id="login-container" class="login-container m-auto">
                    <div class="login-header mb-2">
                        <Riskhub-Logo></Riskhub-Logo>
                    </div>
                    <div class="login-message text-center">
                        <h4 class="my-4">{{ title }}</h4>
                        <p>{{ message }}</p>
                    </div>
                    <vue-element-loading
                        :active="loading"
                        color="#a38023"
                        size="60"
                        backgroundColor="rgba(255,255,255,0)"
                    ></vue-element-loading>
                    <div class="client-select" v-if="clientSelect">
                        <change-client
                                defaultText="Please select a client from the list below"
                                @change="hideClientSelect()">
                        </change-client>
                    </div>
                    <div class="text-center">
                    <b-btn v-show="hasError"
                           @click="doRedirect"
                           variant="primary">
                        Dashboard
                        <font-awesome-icon
                                class="ml-2"
                                :icon="['far', 'arrow-right']">
                        </font-awesome-icon>
                    </b-btn>
                    </div>
                </div>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import User from '@/services/User/User';
import RiskhubLogo from '@/components/Logo/RiskhubLogo';
import ChangeClient from '@/components/User/ChangeClient';

const DEFAULT_ERROR = 'default_error';

export default {
  name: 'Login',
  components: {
    RiskhubLogo,
    ChangeClient,
  },
  data() {
    return {
      title: 'Welcome',
      message: 'Logging you in..',
      loading: true,
      loaded: false,
      errors: {
        too_many_attempts: DEFAULT_ERROR,
      },
      errorDescriptions: {
        'signup disabled': 'Wrong email or password.',
      },
      clientSelect: false,
      clientSelectOptions: {},
      hasError: false,
    };
  },
  computed: {
    isFormValid() {
      return true;
    },
    authLoading() {
      return this.$auth.loading;
    },
    isErrorPage() {
      return this.$route.query.error;
    },
    error() {
      const { error } = this.$route.query;

      if (!error) {
        return '';
      }

      return this.errors[error.toLowerCase()];
    },
    errorDescription() {
      const description = this.$route.query.error_description;

      if (!description) {
        return '';
      }

      return this.errorDescriptions[description.toLowerCase()];
    },
  },
  watch: {
    authLoading: {
      async handler(newAuthLoading) {
        if (!newAuthLoading) {
          this.loaded = true;

          if (!this.isErrorPage) {
            await this.getToken();
          } else if (!this.$route.query.state) {
            await this.logoutAndRedirectToLogin();
          } else {
            // Came here after login to error message (Such as logging in via google auth with no account)
            // Redirect them to auth0 with a custom error message
            await this.logoutAndRedirectToLogin(this.getErrorMessage());
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    async getToken() {
      try {
        const token = await this.$auth.getTokenSilently();
        await this.$store.dispatch('saveToken', {
          token,
          expiresIn: 86400,
        });

        await this.$store.dispatch('fetchUser');

        if (this.userHasNoClients()) {
          await this.logoutAndRedirectToLogin('This account is not assigned to any clients');
          return;
        }

        if (this.userHasClientSet()) {
          await this.doRedirect();
        } else if (this.userHasOneClient()) {
          const clientId = this.getFirstClientId();
          if (!clientId) {
            await this.logoutAndRedirectToLogin('Could not find a valid client for this user');
          } else {
            await this.setClientForUser(clientId);
          }
        } else {
          this.showClientSelection();
        }
      } catch (e) {
        if (e.error) {
          await this.logoutAndRedirectToLogin(e.error_description);
        }
        console.log(e);
      }
    },
    getErrorMessage() {
      let message = this.errorDescription;

      if (!message && this.error) {
        message = this.error;
      }

      // Default error is the one auth0 provides
      if (message === DEFAULT_ERROR || !message) {
        message = this.$route.query.error_description;
      }

      return message;
    },
    redirectToLogin(errorMessage) {
      return new Promise((resolve) => {
        const params = {};
        if (errorMessage) {
          params.authorizationParams = { error_description: errorMessage };
        }

        this.$auth.loginWithRedirect(params);
        resolve();
      });
    },
    userHasClientSet() {
      // console.log('CHECKING IF CLIENT IS SET');
      const user = this.$store.getters.authUser;

      return !!user.currentClientId;
    },
    userHasNoClients() {
      const user = this.$store.getters.authUser;

      return user.clients.length === 0;
    },
    userHasOneClient() {
      const user = this.$store.getters.authUser;

      return user.clients.length === 1;
    },
    getFirstClientId() {
      const user = this.$store.getters.authUser;
      const { clientId } = user.clients[0];

      return clientId;
    },
    async setClientForUser(clientId) {
      await User.changeClient(clientId);

      await (this.$store.dispatch('resetTags'));

      this.$store.dispatch('fetchUser').then(() => {
        this.doRedirect();
        this.loading = false;
      });
    },
    async logoutAndRedirectToLogin(errorMessage) {
      await this.$auth.logout();
      await this.redirectToLogin(errorMessage);
    },
    showClientSelection() {
      this.clientSelect = true;
      this.loading = false;
    },
    async doRedirect() {
      try {
        // Check for stored target URL from pre-auth navigation
        const targetUrl = sessionStorage.getItem('authTargetUrl');
        sessionStorage.removeItem('authTargetUrl');

        if (targetUrl && targetUrl.startsWith('/') && targetUrl !== '/') {
          await this.$router.push(targetUrl);
        } else {
          await this.$router.push({ name: User.getDefaultRouteForPermission(this.$store.getters.permissions) });
        }
      } catch (e) {
        this.message = 'Please click the button below if you are not automatically redirected within a few seconds';
        this.hasError = true;
        this.loading = false;
      }
    },
    async hideClientSelect() {
      this.clientSelect = false;
      await this.$store.dispatch('fetchUser');
      window.location.reload();
    },
  },
};
</script>

<style lang="scss">
    .client-select {
        .dropdown {
            list-style: none;
            max-width: 350px;
            color: #1e212a;
            border: 1px solid rgba(0, 0, 0, 0.15);
            border-radius: 4px;
            padding: 3px 2px 2px 6px;

          a {
            color: #1e212a
          }

            a.dropdown-toggle {
                &:after {
                    margin-top: 0.5em;
                    margin-left: 10px;
                }
            }

            .dropdown-menu {
                margin-top: 8px;
                border-radius: 4px;
                width: 100%;
                max-height: 35vh;
                overflow: auto;
            }
          .dropdown-item:active {
            background-color: #1e212a;
            color: #fff;
          }
        }
    }
</style>

<style lang="scss" scoped>

    .riskhub-logo {
      width: 100px;
      height: 100px;
      margin-left: 0;
      margin-top: 0;
    }


    .login-container {
        position: relative;
        height: 100%;
    }

    .login-container {
        min-height: 580px;
        max-width: 400px;
        padding: 40px;
        background-color: #fff;
        border-radius: 12px;
        border-top: 1px solid #e9e9e9;
        box-shadow: rgba(60, 66, 87, 0.12) 0px 7px 14px 0px, rgba(0, 0, 0, 0.12) 0px 3px 6px 0px;
        transition: visibility 0s, opacity 0.5s linear;
        visibility: visible;
        opacity: 1;
        z-index: 10;
        color: #1e212a;
    }

    .login-header {
        text-align: center;
    }
</style>
